import dayjs from 'dayjs';
import { styled } from '@mui/system';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';

import { useOneMonthTimesheet } from '../hooks';
import { statusVisual } from '../statusTokens';
import DayTooltipContent from './DayTooltipContent';

/**
 * The 1..31 day strip used by the admin monthly timesheet rows.
 * Fixed-size coloured squares (the containers scroll horizontally, so the
 * squares never shrink), day number inside, rich tooltip. Pair with
 * <WeekdayStrip/> in the header for the weekday letters.
 */
const SQUARE_SIZE = 26;
const SEPARATOR = 2;
const WEEKDAY_LETTERS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

const StyledContainer = styled('div')({
    display: 'flex',
    alignItems: 'center',
    width: 'max-content'
});

const StyledSquare = styled('div')(({ theme: { palette }, bg }) => ({
    width: SQUARE_SIZE,
    height: SQUARE_SIZE,
    flex: '0 0 auto',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderRight: `${SEPARATOR}px solid`,
    borderRightColor: palette.border?.secondary || palette.divider,
    backgroundColor: bg,
    fontSize: 11,
    fontWeight: 600,
    lineHeight: 1,
    userSelect: 'none',

    ':last-child': {
        borderRight: 'none'
    },

    ':hover': {
        cursor: 'pointer',
        outline: '2px solid',
        outlineOffset: '-1px',
        outlineColor: palette.primary.main,
        zIndex: 1
    }
}));

const Day = ({ day, showDayNumber }) => {
    const { palette } = useTheme();
    const visual = statusVisual(palette, day.status);
    return (
        <Tooltip title={<DayTooltipContent day={day} />}>
            <StyledSquare bg={visual.bg} style={{ color: visual.text }}>
                {showDayNumber ? day.dayOfMonth : null}
            </StyledSquare>
        </Tooltip>
    );
};

const DaySquares = ({
    entries,
    yearMonth,
    daySheet: daySheetProp,
    showDayNumbers = true,
    // data props that may arrive via spread from row components — keep them
    // off the DOM node
    monthlyTotals,
    reportingManager,
    ...rest
}) => {
    const computedSheet = useOneMonthTimesheet(
        daySheetProp ? [] : entries || [],
        yearMonth
    );
    const daySheet = daySheetProp || computedSheet;

    return (
        <StyledContainer {...rest}>
            {daySheet.map((day) => (
                <Day
                    key={`day-${day.isoDate}`}
                    day={day}
                    showDayNumber={showDayNumbers}
                />
            ))}
        </StyledContainer>
    );
};

/**
 * Weekday letters aligned with a <DaySquares/> strip for the same month —
 * shown in the table header (and above the strips on mobile cards).
 */
const WeekdayStrip = ({ yearMonth, sx }) => {
    const monthDate = dayjs(yearMonth);
    if (!monthDate.isValid()) return null;
    const daysInMonth = monthDate.daysInMonth();

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                width: 'max-content',
                ...sx
            }}
        >
            {Array.from({ length: daysInMonth }, (_, index) => {
                const dayDate = monthDate.date(index + 1);
                return (
                    <Typography
                        key={index}
                        variant='caption'
                        sx={{
                            width: SQUARE_SIZE,
                            flex: '0 0 auto',
                            marginRight:
                                index === daysInMonth - 1
                                    ? 0
                                    : `${SEPARATOR}px`,
                            textAlign: 'center',
                            fontSize: 10,
                            fontWeight: 700,
                            lineHeight: 1,
                            color: 'text.secondary'
                        }}
                    >
                        {WEEKDAY_LETTERS[dayDate.day()]}
                    </Typography>
                );
            })}
        </Box>
    );
};

export { DaySquares as default, DaySquares, WeekdayStrip };
